import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Plus, Link2, Copy, CheckCircle2, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PasswordResetRequests } from "@/components/pawkt/PasswordResetRequests";

const TENANT_WIDE = ["owner", "admin"];
const ROLE_LABEL = { accountant: "Accountant — Billing only", purchase: "Purchase", accounts: "Accounts", finance_manager: "Finance Manager", ca_reviewer: "Internal Auditor — Read Only" };
const ROLE_DESC = {
  accountant: "Customer invoices, payments, refunds and reports. No access to Accounts & Purchase; choose Accounts for vendor bills, payments and reconciliation.",
  purchase: "Create purchase requests, manage permitted vendor information and submit bills for review.",
  accounts: "Verify bills and perform permitted payment and reconciliation operations, subject to separation-of-duties rules.",
  finance_manager: "Manage permitted finance operations, expenses, finance settings and period locking.",
  ca_reviewer: "Review permitted financial records without transaction execution, verification, posting or settings permissions.",
};

function RolePreview({ role }) {
  const custom = role.startsWith("custom:");
  const { data, isError } = useQuery({ queryKey: ["role-preview", role], enabled: !!role, retry: false,
    queryFn: async () => (await api.get("/fin/role-preview", { params: custom ? { custom_role_id: role.slice(7) } : { role } })).data });
  if (!data || isError) return null;
  return (
    <div className="mt-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="team-role-preview">
      <p className="font-semibold text-slate-700">{data.all ? "Full access to everything" : `${Object.values(data.groups).flat().length} permissions`} · {data.branch_scoped ? "limited to the branches you choose" : "all branches"}</p>
      <p>Accounts & Purchase: {data.all ? "all" : data.finance.length ? data.finance.join(", ") : "none"}</p>
      {!!data.execution_powers.length && <p className="text-amber-700" data-testid="team-role-exec">Can verify, approve, execute or confirm money: {data.execution_powers.join(", ")}</p>}
    </div>
  );
}
const STATUS_STYLE = {
  active: "text-emerald-600",
  invited: "text-amber-600",
  deactivated: "text-slate-400",
};

export default function Team() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activation, setActivation] = useState(null); // {url} shown once
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "manager", branch_ids: [], staff_id: "", phone: "" });
  const [resetFor, setResetFor] = useState(null);
  const [tempPwd, setTempPwd] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team/users")).data });
  const { data: staffProfiles } = useQuery({ queryKey: ["staff", ""], queryFn: async () => (await api.get("/staff")).data });
  const linkedTo = Object.fromEntries((data?.users || []).filter((u) => u.staff_id).map((u) => [u.staff_id, u]));
  const branches = data?.branches || [];
  const roles = data?.roles || [];
  const customRoles = data?.custom_roles || [];
  const seats = data?.seats || null;
  const seatLabel = seats ? (seats.staff_limit < 0 ? `${seats.staff_used} staff · unlimited` : `${seats.staff_used} of ${seats.staff_limit} staff`) : "";
  const seatFull = !!seats?.at_limit;

  const showActivation = (path) => { setActivation({ url: window.location.origin + path }); setCopied(false); };
  const reset = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "manager", branch_ids: [], staff_id: "", phone: "" }); };
  const save = useMutation({
    mutationFn: async () => {
      const isCustom = (form.role || "").startsWith("custom:");
      if (editing) {
        return isCustom
          ? api.patch(`/team/users/${editing.id}`, { custom_role_id: form.role.slice(7), branch_ids: form.branch_ids, staff_id: form.staff_id })
          : api.patch(`/team/users/${editing.id}`, { role: form.role, branch_ids: form.branch_ids, custom_role_id: "", staff_id: form.staff_id });
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) throw new Error("Enter a valid email address");
      if ((form.password || "").length < 8) throw new Error("Temporary password must be at least 8 characters");
      const base = { name: form.name, email: form.email, password: form.password, branch_ids: form.branch_ids, staff_id: form.staff_id, phone: form.phone };
      return isCustom
        ? api.post("/team/users", { ...base, role: "receptionist", custom_role_id: form.role.slice(7) })
        : api.post("/team/users", { ...base, role: form.role });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["team"] });
      setOpen(false);
      const act = res?.data?.activation;
      reset();
      if (act) showActivation(act.path);
      else toast.success(editing ? "Staff updated" : "Staff account created — share the temporary password with them");
    },
    onError: (e) => toast.error(e?.response ? apiErr(e) : e.message),
  });
  const resetPwd = useMutation({
    mutationFn: () => api.post(`/team/users/${resetFor.id}/temporary-password`, { password: tempPwd }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); setResetFor(null); setTempPwd(""); toast.success("Temporary password set — they must change it at next login"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const toggleActive = useMutation({
    mutationFn: (u) => api.patch(`/team/users/${u.id}`, { active: !u.active }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const regenLink = useMutation({
    mutationFn: (u) => api.post(`/team/users/${u.id}/activation-link`),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["team"] }); showActivation(res.data.activation.path); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const copyLink = async () => { try { await navigator.clipboard.writeText(activation.url); setCopied(true); toast.success("Activation link copied"); } catch { toast.error("Copy failed — select and copy manually"); } };

  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, role: u.custom_role_id ? `custom:${u.custom_role_id}` : u.role, branch_ids: u.branch_ids || [], staff_id: u.staff_id || "", phone: u.phone || "" }); setOpen(true); };
  const openNew = () => { reset(); setOpen(true); };
  const tenantWide = TENANT_WIDE.includes(form.role);
  const toggleBranch = (id) => setForm((f) => ({ ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id] }));

  return (
    <div>
      <PageHeader title="Staff Management" subtitle={seats ? `Staff, roles & permissions · ${seatLabel} · ${seats.plan} plan` : "Staff, roles & permissions"}>
        <div className="flex items-center gap-3">
          {seats && <span data-testid="team-seat-indicator" className={`rounded-full px-3 py-1 text-xs font-semibold ${seatFull ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{seatLabel}</span>}
          <Button onClick={openNew} disabled={seatFull} data-testid="team-add-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> Add Staff</Button>
        </div>
      </PageHeader>
      <PasswordResetRequests />
      {seatFull && (
        <p data-testid="team-seat-full-hint" className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {seats.over_limit
            ? `Your ${seats.plan} plan includes ${seats.staff_limit} additional staff, but ${seats.staff_used} are active. Existing accounts are kept; new staff and reactivations are blocked until you upgrade or deactivate staff.`
            : `Your ${seats.plan} plan includes ${seats.staff_limit} additional staff (plus the owner). Upgrade to Growth or Multi-Branch for unlimited staff.`}
          {" "}<Link to="/billing" className="font-semibold underline" data-testid="team-upgrade-link">View plans</Link>
        </p>
      )}

      {isLoading ? <Loading /> : (!data?.users?.length ? <Empty icon={Users} title="No team members" /> : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Branch access</th><th className="px-4 py-3">Account status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`team-row-${u.id}`}>
                  <td className="px-4 py-3"><p className="font-semibold text-slate-900">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                  <td className="px-4 py-3 capitalize" data-testid={`team-role-label-${u.id}`}>{u.custom_role_name || ROLE_LABEL[u.role] || u.role}{u.custom_role_name && <span className="ml-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-indigo-600">custom</span>}{u.staff_id && <span className="block text-[11px] normal-case text-slate-500" data-testid={`team-staff-link-${u.id}`}>Staff profile: {(staffProfiles || []).find((s) => s.id === u.staff_id)?.name || "linked"}</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{u.tenant_wide ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">All branches</span> : u.branch_ids.map((b) => branches.find((x) => x.id === b)?.name || b).join(", ") || "—"}</td>
                  <td className="px-4 py-3" data-testid={`team-status-${u.id}`}><span className={`text-xs font-medium capitalize ${STATUS_STYLE[u.account_status] || "text-slate-500"}`}>● {u.account_status || (u.active ? "active" : "deactivated")}</span>{u.must_change_password && u.active && <span data-testid={`team-pwd-pending-${u.id}`} className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">password change pending</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.account_status === "deactivated" ? (
                      <button onClick={() => toggleActive.mutate(u)} disabled={seatFull} title={seatFull ? "Plan staff limit reached" : ""} data-testid={`team-reactivate-${u.id}`} className="text-xs font-medium text-emerald-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-300">Reactivate</button>
                    ) : u.role === "owner" ? <span className="text-xs text-slate-400">Owner</span> : (
                      <>
                        <button onClick={() => openEdit(u)} data-testid={`team-edit-${u.id}`} className="text-xs font-medium text-slate-700 hover:underline mr-3">Change permissions</button>
                        <button onClick={() => { setResetFor(u); setTempPwd(""); }} data-testid={`team-reset-pwd-${u.id}`} className="text-xs font-medium text-slate-700 hover:underline mr-3"><KeyRound className="inline h-3 w-3" /> Temp password</button>
                        {u.account_status === "invited" && (
                          <button onClick={() => regenLink.mutate(u)} data-testid={`team-activation-link-${u.id}`} className="text-xs font-medium text-amber-600 hover:underline mr-3"><Link2 className="inline h-3 w-3" /> Activation link</button>
                        )}
                        <button onClick={() => toggleActive.mutate(u)} data-testid={`team-toggle-${u.id}`} className="text-xs font-medium text-slate-500 hover:underline">Deactivate</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader><DialogTitle>{editing ? "Change role & permissions" : "Add Staff"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && <>
              <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" />
              <Input type="password" autoComplete="new-password" placeholder="Temporary password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-temp-password" />
              <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="team-phone" />
              <p className="text-xs text-slate-400">Share this temporary password with the staff member. They must change it on first login. It is never shown again.</p>
            </>}
            {editing && <p className="text-xs text-slate-500">Permissions come from the role. Edit role permissions in <Link to="/roles" className="underline">Roles</Link>.</p>}
            <div>
              <label className="text-xs font-medium text-slate-500">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role" className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <optgroup label="System roles">
                  {roles.filter((r) => r !== "ca_reviewer" || editing?.role === "ca_reviewer").map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </optgroup>
                {customRoles.length > 0 && (
                  <optgroup label="Custom roles">
                    {customRoles.map((r) => <option key={r.id} value={`custom:${r.id}`}>{r.name}</option>)}
                  </optgroup>
                )}
              </select>
              {ROLE_DESC[form.role] && <p className="mt-1.5 text-xs leading-snug text-slate-600" data-testid="team-role-description"><span className="font-semibold text-slate-700">{ROLE_LABEL[form.role]}</span> — {ROLE_DESC[form.role]}</p>}
              <RolePreview role={form.role} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Staff profile (appointments assigned to this person)</label>
              <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} data-testid="team-staff-profile" className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Not linked</option>
                {(staffProfiles || []).filter((s) => s.active !== false).map((s) => {
                  const taken = linkedTo[s.id] && linkedTo[s.id].id !== editing?.id;
                  return <option key={s.id} value={s.id} disabled={!!taken}>{s.name}{s.role ? ` · ${s.role}` : ""}{taken ? ` (linked to ${linkedTo[s.id].name})` : ""}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Branch access</label>
              {tenantWide ? <p className="mt-1 text-sm text-emerald-700">Tenant-wide (all branches)</p> : (
                <div className="mt-1 space-y-1.5" data-testid="team-branches">
                  {branches.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.branch_ids.includes(b.id)} onChange={() => toggleBranch(b.id)} data-testid={`team-branch-${b.id}`} />
                      {b.name}
                    </label>
                  ))}
                  {!branches.length && <p className="text-xs text-slate-400">No branches yet — add branches in Settings.</p>}
                </div>
              )}
            </div>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="team-save" className="w-full bg-slate-900 text-white">{editing ? "Save changes" : "Create staff account"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent data-testid="reset-pwd-dialog">
          <DialogHeader><DialogTitle>Set temporary password · {resetFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" autoComplete="new-password" placeholder="New temporary password (min 8 characters)" value={tempPwd} onChange={(e) => setTempPwd(e.target.value)} data-testid="reset-pwd-input" />
            <p className="text-xs text-slate-400">Their current sessions end and they must choose a new password at next login.</p>
            <Button onClick={() => resetPwd.mutate()} disabled={resetPwd.isPending || tempPwd.length < 8} data-testid="reset-pwd-save" className="w-full bg-slate-900 text-white">Set temporary password</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activation} onOpenChange={(v) => !v && setActivation(null)}>
        <DialogContent data-testid="activation-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Employee created</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Share this one-time <span className="font-semibold">activation link</span> with the employee. It expires in 24 hours and can only be used once. This link is shown <span className="font-semibold">only now</span> — generate a new one from the Team list if needed.</p>
            <div className="flex items-center gap-2">
              <input readOnly value={activation?.url || ""} data-testid="activation-link-value" className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" onFocus={(e) => e.target.select()} />
              <Button onClick={copyLink} data-testid="activation-copy-btn" className="bg-slate-900 text-white gap-1.5">{copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}</Button>
            </div>
            <p className="text-xs text-slate-400">You will never see the employee's password. They set it themselves on activation.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
